<template>
  <v-dialog v-model="dialog" :max-width="$vuetify.breakpoint.xsOnly ? '' : '1000'" :fullscreen="$vuetify.breakpoint.xsOnly" persistent>
    <v-card v-if="line">
      <v-toolbar dark color="primary">
        <v-toolbar-title class="white--text">{{ $t('inventory.purchase_invoices.book_title') }}</v-toolbar-title>
        <v-spacer />
        <v-btn icon dark :aria-label="$t('close')" @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="pt-5 gradient-icons">
        <div class="subtitle-1">{{ invoice.invoiceNumber }} {{ invoice.vendorName }}: {{ lineText }}</div>
        <div class="text--secondary mb-4">
          {{ $t('inventory.purchase_invoices.net_sum') }}: <strong>{{ formatEuro(line.lineNetSum) }}</strong>
          · {{ $t('inventory.purchase_invoices.booked_value') }}: {{ formatEuro(line.bookedValue) }}
          · {{ $t('inventory.purchase_invoices.remaining') }}:
          <strong :class="remaining < 0 ? 'error--text' : ''">{{ formatEuro(remaining) }}</strong>
        </div>
        <v-form ref="form" v-model="valid">
          <v-row>
            <v-col cols="12" sm="4">
              <v-menu v-model="dateMenu" :close-on-content-click="false" offset-y max-width="290px">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="formatIsoDateFi(transactionDate)"
                    :label="$t('inventory.dispatch_date')"
                    readonly outlined dense hide-details
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs" v-on="on"
                  />
                </template>
                <v-date-picker v-model="transactionDate" no-title locale="fi" :first-day-of-week="1" @input="dateMenu = false" />
              </v-menu>
            </v-col>
          </v-row>

          <div v-for="(row, index) in rows" :key="row.key" class="booking-row pa-3 mb-3">
            <!-- Nimike omalla rivillään, jotta nimi ja tyyppi mahtuvat samalle riville -->
            <div class="d-flex align-center">
              <item-combobox
                :value="row.item"
                :types="trackableTypes"
                :label="$t('inventory.select_item')"
                required
                hide-details="auto"
                class="flex-grow-1"
                @change="item => changeItem(row, item)"
              />
              <v-btn icon color="error" class="ml-2" :aria-label="$t('inventory.remove_row')" :disabled="rows.length === 1" @click="rows.splice(index, 1)">
                <v-icon class="no-gradient">mdi-delete</v-icon>
              </v-btn>
            </div>

            <!-- Käyttö- ja varastoyksikkö kertoimineen: mitä yksikköä syötetään ja mihin se muunnetaan -->
            <div v-if="hasWarehouseUnit(row)" class="d-flex align-center flex-wrap mt-3">
              <div class="body-2 mr-4">
                {{ $t('items.unit') }} <strong>{{ unitLabel(row.item) }}</strong>
                · {{ $t('items.warehouse_unit') }} <strong>{{ row.item.warehouseUnit }}</strong>
                · {{ $t('items.conversion_factor') }}
                <strong>1 {{ unitLabel(row.item) }} = {{ formatQuantity(row.item.conversionFactor) }} {{ row.item.warehouseUnit }}</strong>
              </div>
              <span class="caption text--secondary mr-2">{{ $t('inventory.input_unit') }}</span>
              <v-btn-toggle
                :value="row.unitMode"
                mandatory
                dense
                role="group"
                :aria-label="$t('inventory.input_unit')"
                @change="mode => changeUnitMode(row, mode)"
              >
                <v-btn value="usage" small :aria-pressed="String(row.unitMode === 'usage')">{{ unitLabel(row.item) }}</v-btn>
                <v-btn value="warehouse" small :aria-pressed="String(row.unitMode === 'warehouse')">{{ row.item.warehouseUnit }}</v-btn>
              </v-btn-toggle>
            </div>

            <v-row dense class="mt-2">
              <v-col cols="12" sm="6" md>
                <v-select
                  v-model="row.warehouseId"
                  :items="warehouses"
                  item-text="name"
                  item-value="id"
                  :label="$t('inventory.warehouse')"
                  :rules="[v => !!v || $t('validation.required')]"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col v-if="useWarehouseUnit(row)" cols="12" sm="6" md>
                <numeric-input
                  :key="`amount-${row.key}-${row.amountKey}`"
                  :value="row.amount"
                  :label="$t('inventory.amount_in_unit', { unit: row.item.warehouseUnit })"
                  :options="numberOptions"
                  :suffix="row.item.warehouseUnit"
                  :hint="quantityHint(row)"
                  persistent-hint
                  outlined
                  dense
                  hide-details="auto"
                  @input="v => changeQuantity(row, 'amount', v)"
                />
              </v-col>
              <template v-else>
                <v-col cols="6" sm="6" md>
                  <v-text-field
                    :value="row.pieces"
                    :label="piecesLabel(row)"
                    type="number"
                    min="0"
                    step="any"
                    :suffix="isMeterItem(row) ? PIECES_UNIT : unitLabel(row.item)"
                    :hint="isMeterItem(row) ? '' : quantityHint(row)"
                    :disabled="!row.item"
                    persistent-hint
                    outlined
                    dense
                    hide-details="auto"
                    @input="v => changeQuantity(row, 'pieces', v)"
                  />
                </v-col>
                <v-col v-if="isMeterItem(row)" cols="6" sm="6" md>
                  <v-text-field
                    :value="row.unitQuantity"
                    :label="$t('inventory.meters_per_piece')"
                    type="number"
                    step="0.1"
                    min="0.1"
                    suffix="m"
                    :hint="quantityHint(row)"
                    persistent-hint
                    outlined
                    dense
                    hide-details="auto"
                    @input="v => changeQuantity(row, 'unitQuantity', v)"
                  />
                </v-col>
              </template>
              <v-col cols="12" sm="6" md>
                <numeric-input
                  :key="`price-${row.key}-${row.priceKey}`"
                  :value="row.unitPrice"
                  :label="$t('inventory.unit_cost_in_unit', { unit: enteredUnit(row) || '–' })"
                  :options="numberOptions"
                  :hint="priceHint(row)"
                  :disabled="!row.item"
                  suffix="€"
                  persistent-hint
                  outlined
                  dense
                  hide-details="auto"
                  @input="v => changePrice(row, v)"
                />
              </v-col>
              <v-col cols="12" sm="6" md>
                <numeric-input
                  :key="`value-${row.key}-${row.valueKey}`"
                  :value="row.value"
                  :label="$t('inventory.purchase_invoices.value_vat0')"
                  suffix="€"
                  outlined
                  dense
                  hide-details="auto"
                  @input="v => changeValue(row, v)"
                />
              </v-col>
            </v-row>
          </div>
          <v-btn text color="primary" class="mt-2" @click="addRow(null)">
            <v-icon left>mdi-plus</v-icon>
            {{ $t('inventory.add_item') }}
          </v-btn>
        </v-form>
      </v-card-text>
      <v-divider />
      <v-card-actions :class="$vuetify.breakpoint.xsOnly && 'flex-column-reverse'">
        <v-spacer />
        <v-btn text :block="$vuetify.breakpoint.xsOnly" @click="dialog = false">{{ $t('cancel') }}</v-btn>
        <v-btn
          color="primary"
          :block="$vuetify.breakpoint.xsOnly"
          :class="$vuetify.breakpoint.xsOnly && 'mb-2'"
          :disabled="!valid || !rowsComplete"
          :loading="saving"
          @click="save"
        >
          {{ $t('inventory.purchase_invoices.book') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import inventoryApi from '@/api/inventory';
import netvisorApi from '@/api/netvisor';
import ItemCombobox from '@/components/ItemCombobox';
import NumericInput from '@/components/NumericInput';
import { activeTypeIds } from '@/helpers/itemTypes';
import { PIECES_UNIT, formatIsoDateFi, formatQuantity, unitLabel } from '@/helpers/inventoryFormat';
import { defaultWarehouseId } from '@/helpers/selectedWarehouse';

let nextRowKey = 1;

const round2 = v => Math.round(v * 100) / 100;
const round4 = v => Math.round(v * 10000) / 10000;
const toNumber = v => (v === null || v === undefined || v === '' ? null : Number(v));

export default {
  name: 'PurchaseLineBookingDialog',
  components: { ItemCombobox, NumericInput },
  data() {
    return {
      dialog: false,
      invoice: null,
      line: null,
      warehouses: [],
      transactionDate: null,
      dateMenu: false,
      rows: [],
      valid: false,
      saving: false,
      PIECES_UNIT,
      numberOptions: {
        decimalCharacter: ',',
        digitGroupSeparator: ' ',
        decimalPlaces: 4,
        allowDecimalPadding: false,
        emptyInputBehavior: 'null',
      },
    };
  },
  computed: {
    trackableTypes() {
      return activeTypeIds(type => type.trackInventory);
    },
    lineText() {
      const line = this.line;
      return line ? line.description || line.productName || line.productCode || '' : '';
    },
    remaining() {
      if (!this.line || this.line.lineNetSum === null || this.line.lineNetSum === undefined) return null;
      const entered = this.rows.reduce((sum, row) => sum + (Number(row.value) || 0), 0);
      return Number(this.line.lineNetSum) - Number(this.line.bookedValue || 0) - entered;
    },
    rowsComplete() {
      return this.rows.every(row => row.item && row.warehouseId && this.quantityOf(row) > 0
        && row.value !== null && row.value !== undefined && row.value >= 0);
    },
  },
  methods: {
    async open(invoice, line) {
      this.invoice = invoice;
      this.line = line;
      this.transactionDate = invoice.invoiceDate ? String(invoice.invoiceDate).substr(0, 10) : new Date().toISOString().substr(0, 10);
      this.rows = [];
      this.dialog = true;
      try {
        this.warehouses = await inventoryApi.getWarehouses();
      } catch (error) {
        console.error('Error loading warehouses:', error);
      }
      const unbooked = line.lineNetSum !== null && line.lineNetSum !== undefined
        ? Math.max(0, Math.round((Number(line.lineNetSum) - Number(line.bookedValue || 0)) * 100) / 100)
        : null;
      this.addRow(unbooked);
    },

    addRow(value) {
      this.rows.push({
        key: nextRowKey++,
        item: null,
        warehouseId: defaultWarehouseId(this.warehouses),
        // usage = käyttöyksikkö (kpl × m), warehouse = varastoyksikkö (esim. kg)
        unitMode: 'usage',
        pieces: null,
        unitQuantity: null,
        amount: null,
        // À-hinta syöttöyksikköä kohti
        unitPrice: null,
        value,
        // Kumpi hinnoista on käyttäjän antama; toinen lasketaan siitä ja määrästä
        anchor: 'value',
        // NumericInput ei seuraa ulkoisia muutoksia: laskettu kenttä luodaan uudelleen avaimen vaihdolla
        amountKey: 0,
        priceKey: 0,
        valueKey: 0,
      });
    },

    unitLabel,
    formatQuantity,
    formatIsoDateFi,

    isMeterItem(row) {
      return unitLabel(row.item) === 'm';
    },

    hasWarehouseUnit(row) {
      return !!(row.item && row.item.warehouseUnit && row.item.conversionFactor);
    },

    useWarehouseUnit(row) {
      return this.hasWarehouseUnit(row) && row.unitMode === 'warehouse';
    },

    enteredUnit(row) {
      if (!row.item) return '';
      return this.useWarehouseUnit(row) ? row.item.warehouseUnit : unitLabel(row.item);
    },

    piecesLabel(row) {
      const unit = unitLabel(row.item);
      return this.isMeterItem(row) || unit === PIECES_UNIT ? this.$t('inventory.pieces') : this.$t('inventory.amount_in_unit', { unit });
    },

    // Määrä syöttöyksikössä
    enteredQuantity(row) {
      if (this.useWarehouseUnit(row)) return row.amount;
      if (!row.pieces) return null;
      if (this.isMeterItem(row)) return row.unitQuantity ? row.pieces * row.unitQuantity : null;
      return row.pieces;
    },

    // Määrä käyttöyksikössä, joka tallennetaan
    quantityOf(row) {
      const quantity = this.enteredQuantity(row);
      if (!quantity) return null;
      return this.useWarehouseUnit(row) ? quantity / row.item.conversionFactor : quantity;
    },

    // Syötetty määrä toisessa yksikössä, esim. "= 331 m" tai "= 150 m · 2 175 kg"
    quantityHint(row) {
      const quantity = this.quantityOf(row);
      if (!quantity) return '';
      const unit = unitLabel(row.item);
      if (this.useWarehouseUnit(row)) return `= ${formatQuantity(quantity)} ${unit}`;
      const parts = this.isMeterItem(row) ? [`${formatQuantity(quantity)} ${unit}`] : [];
      if (this.hasWarehouseUnit(row)) parts.push(`${formatQuantity(quantity * row.item.conversionFactor)} ${row.item.warehouseUnit}`);
      return parts.length ? `= ${parts.join(' · ')}` : '';
    },

    // À-hinta toisessa yksikössä, esim. "= 18,13 €/m"
    priceHint(row) {
      if (!this.hasWarehouseUnit(row) || row.unitPrice === null || row.unitPrice === undefined) return '';
      const factor = row.item.conversionFactor;
      return this.useWarehouseUnit(row)
        ? `= ${this.formatEuro(row.unitPrice * factor)}/${unitLabel(row.item)}`
        : `= ${this.formatEuro(row.unitPrice / factor)}/${row.item.warehouseUnit}`;
    },

    // Nimikkeen vaihto: yksikkö oletukseen (varastoyksikkö, jos nimikkeellä on se) ja määrät tyhjiksi. Arvo säilyy.
    changeItem(row, item) {
      row.item = item || null;
      row.unitMode = this.hasWarehouseUnit(row) ? 'warehouse' : 'usage';
      row.pieces = null;
      row.unitQuantity = null;
      row.amount = null;
      row.amountKey++;
      row.unitPrice = null;
      row.anchor = 'value';
      row.priceKey++;
    },

    changeQuantity(row, field, value) {
      row[field] = toNumber(value);
      this.recalculate(row);
    },

    changePrice(row, price) {
      row.unitPrice = price;
      // Tyhjennetty à-hinta palauttaa arvon määrääväksi, mutta à-hintaa ei täytetä kesken kirjoituksen
      row.anchor = price === null ? 'value' : 'price';
      const quantity = this.enteredQuantity(row);
      if (price !== null && quantity > 0) {
        row.value = round2(price * quantity);
        row.valueKey++;
      }
    },

    changeValue(row, value) {
      row.value = value;
      row.anchor = 'value';
      this.recalculate(row);
    },

    // Määrän tai arvon muutos laskee uudelleen sen hinnan, jota käyttäjä ei ole antanut
    recalculate(row) {
      const quantity = this.enteredQuantity(row);
      if (row.anchor === 'price') {
        row.value = quantity > 0 && row.unitPrice !== null ? round2(row.unitPrice * quantity) : null;
        row.valueKey++;
      } else {
        row.unitPrice = quantity > 0 && row.value !== null && row.value !== undefined ? round4(row.value / quantity) : null;
        row.priceKey++;
      }
    },

    // Yksikön vaihto muuntaa määrän ja à-hinnan toiseen yksikköön, jolloin rivin arvo säilyy
    changeUnitMode(row, mode) {
      if (!mode || mode === row.unitMode) return;
      const factor = row.item.conversionFactor;
      const quantity = this.quantityOf(row);
      if (mode === 'warehouse') {
        row.amount = quantity ? round4(quantity * factor) : null;
        if (row.unitPrice !== null) row.unitPrice = round4(row.unitPrice / factor);
      } else {
        // Metrinimikkeen kappalemäärä tiedetään vain, jos metriä per kappale on annettu
        if (this.isMeterItem(row)) row.pieces = quantity && row.unitQuantity ? round4(quantity / row.unitQuantity) : null;
        else row.pieces = quantity ? round4(quantity) : null;
        if (row.unitPrice !== null) row.unitPrice = round4(row.unitPrice * factor);
      }
      row.unitMode = mode;
      row.amountKey++;
      row.priceKey++;
      if (row.anchor === 'value') this.recalculate(row);
    },

    async save() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      try {
        await netvisorApi.bookWarehouseLine(this.invoice.netvisorKey, this.line.netvisorLineKey, {
          transactionDate: this.transactionDate,
          // Määrä käyttöyksikössä; varastoyksikössä syötetyllä rivillä ei ole kappaleita
          rows: this.rows.map(row => {
            const byPieces = !this.useWarehouseUnit(row);
            return {
              itemId: row.item.id,
              warehouseId: row.warehouseId,
              quantity: this.quantityOf(row),
              pieces: byPieces ? row.pieces : null,
              unitQuantity: byPieces && this.isMeterItem(row) ? row.unitQuantity : null,
              value: row.value,
            };
          }),
        });
        this.$store.dispatch('showNotification', { color: 'success', message: this.$t('inventory.purchase_invoices.booked'), timeOut: 2000 });
        this.dialog = false;
        this.$emit('saved');
      } catch (error) {
        console.error('Error booking purchase invoice line:', error);
        this.$store.dispatch('showNotification', { color: 'error', message: this.$t('inventory.purchase_invoices.book_failed'), timeOut: 3000 });
      } finally {
        this.saving = false;
      }
    },

    formatEuro(value) {
      if (value === null || value === undefined) return '–';
      return Number(value).toLocaleString('fi-FI', { style: 'currency', currency: 'EUR' });
    },
  },
};
</script>

<style scoped>
.booking-row {
  background: rgba(0, 0, 0, 0.03);
  border-radius: 4px;
}
</style>
